<template>
  <div
    class="image-viewer-wrapper"
    :class="{ 'section-mode': !showControls, fullscreen: isFullscreen }"
  >
    <div v-if="showControls && !isFullscreen" class="gallery-controls">
      <button @click="close" class="close-button">Sulje galleria</button>
      <div>
        <button @click="prevImage" :disabled="currentIndex === 0">
          Edellinen
        </button>
        <span>{{ currentIndex + 1 }} / {{ images.length }}</span>
        <button
          @click="nextImage"
          :disabled="currentIndex === images.length - 1"
        >
          Seuraava
        </button>
      </div>
    </div>
    <div
      class="image-container"
      @mousemove="handleMouseMove"
      @mouseleave="hideNavigation"
      @click="handleImageClick"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      tabindex="0"
      @keydown="handleKeydown"
      ref="imageContainer"
      :style="{ height: dynamicHeight }"
      :class="{ 'height-animating': isHeightAnimating }"
    >
      <div
        v-if="showLeftArrow"
        class="nav-arrow nav-arrow-left"
        @click.stop="prevImage"
      >
        ‹
      </div>
      <div
        v-if="showRightArrow"
        class="nav-arrow nav-arrow-right"
        @click.stop="nextImage"
      >
        ›
      </div>
      <div
        class="carousel-container"
        :style="{
          transform: `translateX(${carouselTranslateX}px)`,
          transition: isAnimating
            ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            : 'none',
        }"
      >
        <div
          v-for="(image, index) in images"
          :key="index"
          class="carousel-item"
          :class="{
            active: index === currentIndex,
            'visible-during-animation': isAnimating || isSwiping,
          }"
        >
          <img
            :src="image.url"
            :alt="`Submission image ${index + 1}`"
            @load="index === currentIndex ? onCurrentImageLoad() : undefined"
            class="carousel-image"
            :class="{ 'current-image-sizing': index === currentIndex }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

const props = defineProps({
  images: {
    type: Array as () => { url: string }[],
    required: true,
  },
  initialImage: {
    type: Object as () => { url: string },
    required: true,
  },
  showControls: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["close", "image-changed"]);

const currentIndex = ref(0);
const imageContainer = ref<HTMLElement | null>(null);
const showLeftArrow = ref(false);
const showRightArrow = ref(false);
const imageBounds = ref({ left: 0, right: 0, width: 0 });

// Carousel handling variables
const carouselTranslateX = ref(0);
const isAnimating = ref(false);
const isSwiping = ref(false);
const touchStartX = ref(0);
const swipeTranslateX = ref(0);

// Dynamic height handling
const currentImageHeight = ref(0);
const isHeightAnimating = ref(false);

// Fullscreen handling
const isFullscreen = ref(false);
const showZoomCursor = ref(false);
const lastImageRelativeMousePercent = ref(0.5); // Store as percentage of image width (0-1, can be negative)

const containerWidth = ref(0);
const itemWidth = computed(() => {
  if (isFullscreen.value) {
    // In fullscreen, use viewport width without gap since we want perfect positioning
    return window.innerWidth;
  }
  return containerWidth.value + 40; // Container width + gap for normal mode
});
const dynamicHeight = computed(() =>
  currentImageHeight.value > 0 ? `${currentImageHeight.value}px` : "auto",
);

const findInitialIndex = () => {
  const index = props.images.findIndex(
    (img) => img.url === props.initialImage.url,
  );
  currentIndex.value = index !== -1 ? index : 0;
};

watch(() => props.initialImage, findInitialIndex, { immediate: true });

watch(currentIndex, () => {
  if (!isSwiping.value) {
    updateCarouselPosition();
  }
  // Emit the current image when it changes
  if (props.images[currentIndex.value]) {
    emit("image-changed", props.images[currentIndex.value]);
  }
});

watch(itemWidth, () => {
  updateCarouselPosition();
});

const updateCarouselPosition = () => {
  const targetPosition = -currentIndex.value * itemWidth.value;
  carouselTranslateX.value = targetPosition + swipeTranslateX.value;
};

const animateToPosition = () => {
  isAnimating.value = true;
  swipeTranslateX.value = 0;
  updateCarouselPosition();

  // Update height after position animation starts
  setTimeout(() => {
    updateImageHeight(true);
  }, 50);

  setTimeout(() => {
    isAnimating.value = false;
  }, 400);
};

const prevImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
    animateToPosition();
    setTimeout(() => updateCursorState(), 50); // Update cursor after animation starts
  }
};

const nextImage = () => {
  if (currentIndex.value < props.images.length - 1) {
    currentIndex.value++;
    animateToPosition();
    setTimeout(() => updateCursorState(), 50); // Update cursor after animation starts
  }
};

const close = () => {
  emit("close");
};

const calculateImageBounds = () => {
  if (!imageContainer.value) return;

  const container = imageContainer.value;
  const activeImage = container.querySelector(
    ".carousel-item.active .carousel-image",
  ) as HTMLImageElement;

  if (!activeImage) return;

  const containerRect = container.getBoundingClientRect();
  const imageRect = activeImage.getBoundingClientRect();

  imageBounds.value = {
    left: imageRect.left - containerRect.left,
    right: imageRect.right - containerRect.left,
    width: imageRect.width,
  };
};

const calculateCurrentImageHeight = () => {
  if (!imageContainer.value) return;

  const activeImage = imageContainer.value.querySelector(
    ".carousel-item.active .carousel-image",
  ) as HTMLImageElement;

  if (!activeImage || !activeImage.naturalWidth || !activeImage.naturalHeight)
    return;

  const containerWidth = imageContainer.value.clientWidth;
  const aspectRatio = activeImage.naturalHeight / activeImage.naturalWidth;
  const calculatedHeight = containerWidth * aspectRatio;

  // Apply max-height constraints based on fullscreen mode
  let maxHeight;
  if (isFullscreen.value) {
    maxHeight = window.innerHeight;
  } else {
    // Allow up to 1.5x the container width to prevent extremely tall sections
    // while still giving portrait images room to display properly
    maxHeight = containerWidth * 1.5;
  }
  const newHeight = Math.min(calculatedHeight, maxHeight);

  return newHeight;
};

const updateImageHeight = (animate = true) => {
  const newHeight = calculateCurrentImageHeight();
  if (!newHeight || newHeight === currentImageHeight.value) return;

  if (animate && currentImageHeight.value > 0) {
    isHeightAnimating.value = true;
    setTimeout(() => {
      isHeightAnimating.value = false;
    }, 400);
  }

  currentImageHeight.value = newHeight;
};

const updateContainerWidth = () => {
  if (imageContainer.value) {
    containerWidth.value = imageContainer.value.clientWidth;
  }
};

const onCurrentImageLoad = () => {
  updateContainerWidth();
  calculateImageBounds();
  updateImageHeight(false); // Don't animate on initial load
};

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;

  if (isFullscreen.value) {
    // Enable fullscreen
    document.body.style.overflow = "hidden";
  } else {
    // Disable fullscreen
    document.body.style.overflow = "";
  }

  // Recalculate dimensions for new mode
  setTimeout(() => {
    updateContainerWidth();
    updateImageHeight(false);
    updateCarouselPosition();
    // Wait a bit more for layout to stabilize before updating cursor
    setTimeout(() => {
      updateCursorState();
    }, 50);
  }, 100);
};

const handleMouseMove = (event: MouseEvent) => {
  if (props.images.length < 1) return;

  const containerRect = (
    event.currentTarget as HTMLElement
  ).getBoundingClientRect();
  const mouseX = event.clientX - containerRect.left;

  calculateImageBounds();

  // Store position as percentage of image width (can be negative for left navigation area)
  const imageRelativeMouseX = mouseX - imageBounds.value.left;
  lastImageRelativeMousePercent.value =
    imageRelativeMouseX / imageBounds.value.width;

  const imageLeft = imageBounds.value.left;
  const imageRight = imageBounds.value.right;
  const imageWidth = imageBounds.value.width;

  const defaultZoneWidth = containerWidth.value * 0.33;
  const leftEmptySpace = imageLeft;
  const rightEmptySpace = containerWidth.value - imageRight;

  const leftZoneWidth = Math.max(
    defaultZoneWidth,
    leftEmptySpace + imageWidth * 0.1,
  );
  const rightZoneWidth = Math.max(
    defaultZoneWidth,
    rightEmptySpace + imageWidth * 0.1,
  );

  showLeftArrow.value = mouseX <= leftZoneWidth && currentIndex.value > 0;
  showRightArrow.value =
    mouseX >= containerWidth.value - rightZoneWidth &&
    currentIndex.value < props.images.length - 1;

  // Check if mouse is in the middle area (fixed center zone regardless of navigation availability)
  const centerStart = leftZoneWidth;
  const centerEnd = containerWidth.value - rightZoneWidth;
  const inMiddleArea = mouseX > centerStart && mouseX < centerEnd;
  showZoomCursor.value = inMiddleArea;

  const container = event.currentTarget as HTMLElement;
  if (showLeftArrow.value || showRightArrow.value) {
    container.style.cursor = "pointer";
  } else if (inMiddleArea) {
    container.style.cursor = isFullscreen.value ? "zoom-out" : "zoom-in";
  } else {
    container.style.cursor = "default";
  }
};

const hideNavigation = () => {
  showLeftArrow.value = false;
  showRightArrow.value = false;
  showZoomCursor.value = false;
  if (imageContainer.value) {
    imageContainer.value.style.cursor = "default";
  }
};

const updateCursorState = () => {
  if (!imageContainer.value) return;

  // Recalculate current image bounds for new layout
  calculateImageBounds();

  // Convert stored percentage back to pixels, then to container-relative position
  const imageRelativeMouseX =
    lastImageRelativeMousePercent.value * imageBounds.value.width;
  const mouseX = imageRelativeMouseX + imageBounds.value.left;

  // Check if the calculated position is within container bounds
  if (mouseX < 0 || mouseX > containerWidth.value) {
    hideNavigation();
    return;
  }

  updateCursorForPosition(mouseX);
};

const updateCursorForPosition = (mouseX: number) => {
  if (!imageContainer.value) return;

  // Recalculate zones with current mouse position
  calculateImageBounds();

  const imageLeft = imageBounds.value.left;
  const imageRight = imageBounds.value.right;
  const imageWidth = imageBounds.value.width;

  const defaultZoneWidth = containerWidth.value * 0.33;
  const leftEmptySpace = imageLeft;
  const rightEmptySpace = containerWidth.value - imageRight;

  const leftZoneWidth = Math.max(
    defaultZoneWidth,
    leftEmptySpace + imageWidth * 0.1,
  );
  const rightZoneWidth = Math.max(
    defaultZoneWidth,
    rightEmptySpace + imageWidth * 0.1,
  );

  showLeftArrow.value = mouseX <= leftZoneWidth && currentIndex.value > 0;
  showRightArrow.value =
    mouseX >= containerWidth.value - rightZoneWidth &&
    currentIndex.value < props.images.length - 1;

  const centerStart = leftZoneWidth;
  const centerEnd = containerWidth.value - rightZoneWidth;
  const inMiddleArea = mouseX > centerStart && mouseX < centerEnd;
  showZoomCursor.value = inMiddleArea;

  // Debug logging - remove when fixed
  console.log("updateCursorForPosition:", {
    mouseX,
    containerWidth: containerWidth.value,
    imageLeft,
    imageRight,
    imageWidth,
    leftZoneWidth,
    rightZoneWidth,
    centerStart,
    centerEnd,
    inMiddleArea,
    showZoomCursor: showZoomCursor.value,
    isFullscreen: isFullscreen.value,
  });

  if (showLeftArrow.value || showRightArrow.value) {
    imageContainer.value.style.cursor = "pointer";
  } else if (inMiddleArea) {
    imageContainer.value.style.cursor = isFullscreen.value
      ? "zoom-out"
      : "zoom-in";
  } else {
    imageContainer.value.style.cursor = "default";
  }
};

const handleImageClick = () => {
  if (showLeftArrow.value) {
    prevImage();
  } else if (showRightArrow.value) {
    nextImage();
  } else if (showZoomCursor.value) {
    toggleFullscreen();
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "ArrowLeft") prevImage();
  if (event.key === "ArrowRight") nextImage();
  if (event.key === "Escape") {
    if (isFullscreen.value) {
      toggleFullscreen();
    } else {
      close();
    }
  }
};

// Touch handling functions
const handleTouchStart = (event: TouchEvent) => {
  if (props.images.length <= 1) return;
  isSwiping.value = true;
  touchStartX.value = event.touches[0].clientX;
  swipeTranslateX.value = 0;
  isAnimating.value = false;
};

const handleTouchMove = (event: TouchEvent) => {
  if (!isSwiping.value) return;

  const currentX = event.touches[0].clientX;
  const deltaX = currentX - touchStartX.value;

  // Limit the swipe to maximum one image width in either direction
  const maxSwipe = itemWidth.value;
  const constrainedDeltaX = Math.max(-maxSwipe, Math.min(maxSwipe, deltaX));

  if (
    (currentIndex.value === 0 && constrainedDeltaX > 0) ||
    (currentIndex.value === props.images.length - 1 && constrainedDeltaX < 0)
  ) {
    swipeTranslateX.value = constrainedDeltaX / 3; // Resistance at edges
  } else {
    swipeTranslateX.value = constrainedDeltaX;
  }

  updateCarouselPosition();
};

const handleTouchEnd = () => {
  if (!isSwiping.value) return;
  isSwiping.value = false;

  // Use 33% of container width as threshold for completing the swipe
  const swipeThreshold = containerWidth.value * 0.33;
  const swipeDistance = Math.abs(swipeTranslateX.value);

  // Only change image if swipe distance exceeds threshold and we can move in that direction
  if (swipeDistance > swipeThreshold) {
    if (
      swipeTranslateX.value < 0 &&
      currentIndex.value < props.images.length - 1
    ) {
      // Swiping left (next image)
      currentIndex.value++;
    } else if (swipeTranslateX.value > 0 && currentIndex.value > 0) {
      // Swiping right (previous image)
      currentIndex.value--;
    }
  }

  // Always animate to final position (either new image or back to current)
  animateToPosition();
};

const handleResize = () => {
  setTimeout(() => {
    updateContainerWidth();
    calculateImageBounds();
    updateImageHeight(false);
    updateCarouselPosition();
  }, 100);
};

onMounted(() => {
  if (imageContainer.value && !props.showControls) {
    imageContainer.value.focus();
  }

  // Add resize listener
  window.addEventListener("resize", handleResize);

  setTimeout(() => {
    updateContainerWidth();
    calculateImageBounds();
    updateCarouselPosition();
    updateImageHeight(false);
  }, 100);
});

onUnmounted(() => {
  // Clean up resize listener
  window.removeEventListener("resize", handleResize);
});
</script>

<style scoped>
.image-viewer-wrapper {
  width: 100%;
  padding: 1rem;
  background-color: #333;
  color: white;
  text-align: center;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden; /* Contain the slider */
}

.image-viewer-wrapper.section-mode {
  background-color: transparent;
  color: inherit;
  padding: 1rem;
  margin-bottom: 0;
}

.image-viewer-wrapper.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  color: white;
  padding: 0;
  margin: 0;
  z-index: 9999;
  border-radius: 0;
}

.gallery-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.gallery-controls button {
  margin: 0 10px;
  background-color: #555;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.gallery-controls button:disabled {
  background-color: #444;
  cursor: not-allowed;
  opacity: 0.6;
}

.image-container {
  position: relative;
  outline: none;
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  overflow: hidden;
  width: 100%;
}

.image-container.height-animating {
  transition: height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.carousel-container {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
}

.carousel-item {
  flex: 0 0 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 40px;
  opacity: 0;
  transition: opacity 0.4s ease;
  height: 100%;
}

.fullscreen .carousel-item {
  margin-right: 0;
}

.carousel-item.active {
  opacity: 1;
}

.carousel-item.visible-during-animation {
  opacity: 1;
  transition: none;
}

.carousel-image {
  max-width: 100%;
  width: auto;
  height: 100%;
  object-fit: contain;
  border-radius: 4px;
  flex-shrink: 0;
}

.carousel-image.current-image-sizing {
  height: auto;
  max-width: 100%;
}

.fullscreen .carousel-image.current-image-sizing {
  max-height: 100vh;
  max-width: 100vw;
}

.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 3rem;
  font-weight: bold;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  z-index: 10;
}

.nav-arrow:hover {
  background-color: rgba(0, 0, 0, 0.7);
  transform: translateY(-50%) scale(1.1);
}

.nav-arrow-left {
  left: 20px;
}

.nav-arrow-right {
  right: 20px;
}
</style>
